import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import {
  Camera, Receipt, DollarSign, Boxes, ListChecks, Droplet, Shield, ClipboardCheck, Check, Circle
} from "lucide-react";
import { hasPermission } from "@/lib/permissions";

const MIN_PHOTOS = 5;

const SECTION_ICONS = {
  damage: ClipboardCheck,
  photos: Camera,
  receipts: Receipt,
  expenses: DollarSign,
  inventory: Boxes,
  checklist: ListChecks,
  moisture: Droplet,
  contentguard: Shield,
};

const scrollToAnchor = (anchorId) => {
  const el = document.getElementById(anchorId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-2", "ring-amber-300");
    setTimeout(() => el.classList.remove("ring-2", "ring-amber-300"), 1500);
  }
};

export default function SectionProgress({ job, user, refreshKey = 0 }) {
  const [counts, setCounts] = useState({
    damage: 0,
    photos: 0,
    receipts: 0,
    expenses: 0,
    inventory: 0,
    checklist_ready: false,
    moisture: 0,
    contentguard_active: false,
  });
  const [loading, setLoading] = useState(true);

  const showMoisture = hasPermission(user, "tech.moisture");
  const showContentGuard = hasPermission(user, "tech.contentguard");
  const isInProgress = job.current_status === "in_progress";

  const fetchCounts = useCallback(async () => {
    if (!job?.id) return;
    const safeLen = (resp, key = "items") =>
      Array.isArray(resp?.data?.[key]) ? resp.data[key].length :
      Array.isArray(resp?.data) ? resp.data.length :
      typeof resp?.data?.count === "number" ? resp.data.count : 0;

    const requests = [
      API.get(`/photos/damage-assessment/job/${job.id}`).catch(() => null),
      API.get(`/photos/job/${job.id}`).catch(() => null),
      API.get(`/photos/receipts/job/${job.id}`).catch(() => null),
      API.get(`/expenses/job/${job.id}`).catch(() => null),
      API.get(`/inventory/job/${job.id}`).catch(() => null),
      API.get(`/checklists/job/${job.id}/completion-ready`).catch(() => null),
      showMoisture ? API.get(`/moisture/job/${job.id}`).catch(() => null) : Promise.resolve(null),
      showContentGuard ? API.get(`/contents/session/${job.id}`).catch(() => null) : Promise.resolve(null),
    ];

    const [damageR, photosR, receiptsR, expensesR, inventoryR, checklistR, moistureR, contentR] =
      await Promise.all(requests);

    setCounts({
      damage: safeLen(damageR, "photos") || safeLen(damageR),
      photos: safeLen(photosR, "photos") || safeLen(photosR),
      receipts: safeLen(receiptsR, "receipts") || safeLen(receiptsR),
      expenses: safeLen(expensesR, "expenses") || safeLen(expensesR),
      inventory: safeLen(inventoryR, "items") || safeLen(inventoryR),
      checklist_ready: !!checklistR?.data?.ready || !!checklistR?.data?.can_complete,
      moisture: safeLen(moistureR, "readings") || safeLen(moistureR),
      contentguard_active: !!contentR?.data?.session?.id || !!contentR?.data?.id,
    });
    setLoading(false);
  }, [job?.id, showMoisture, showContentGuard]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 8000);
    return () => clearInterval(interval);
  }, [fetchCounts, refreshKey]);

  // Build sections relevant to current job status
  const sections = [];

  if (isInProgress) {
    sections.push({
      key: "damage",
      label: "Damage",
      anchor: `section-damage-${job.id}`,
      done: counts.damage >= 1,
      detail: `${counts.damage} photo${counts.damage === 1 ? "" : "s"}`,
    });
  }

  sections.push({
    key: "photos",
    label: "Photos",
    anchor: `section-photos-${job.id}`,
    done: counts.photos >= MIN_PHOTOS,
    detail: `${counts.photos} / ${MIN_PHOTOS}`,
  });

  sections.push({
    key: "receipts",
    label: "Receipts",
    anchor: `section-receipts-${job.id}`,
    done: counts.receipts >= 1,
    detail: counts.receipts >= 1 ? `${counts.receipts}` : "none",
  });

  sections.push({
    key: "expenses",
    label: "Expenses",
    anchor: `section-expenses-${job.id}`,
    done: counts.expenses >= 1,
    detail: counts.expenses >= 1 ? `${counts.expenses}` : "none",
  });

  if (isInProgress) {
    sections.push({
      key: "inventory",
      label: "Inventory",
      anchor: `section-inventory-${job.id}`,
      done: counts.inventory >= 1,
      detail: counts.inventory >= 1 ? `${counts.inventory}` : "none",
    });
    sections.push({
      key: "checklist",
      label: "Checklist",
      anchor: `section-checklist-${job.id}`,
      done: counts.checklist_ready,
      detail: counts.checklist_ready ? "ready" : "open",
    });
    if (showMoisture) {
      sections.push({
        key: "moisture",
        label: "Moisture",
        anchor: `section-moisture-${job.id}`,
        done: counts.moisture >= 1,
        detail: counts.moisture >= 1 ? `${counts.moisture}` : "none",
      });
    }
    if (showContentGuard) {
      sections.push({
        key: "contentguard",
        label: "ContentGuard",
        anchor: `section-contentguard-${job.id}`,
        done: counts.contentguard_active,
        detail: counts.contentguard_active ? "active" : "off",
      });
    }
  }

  const completed = sections.filter((s) => s.done).length;
  const total = sections.length || 1;
  const pct = Math.round((completed / total) * 100);

  return (
    <div
      className="mb-3 rounded-xl border border-zinc-200 bg-gradient-to-br from-white to-zinc-50 p-3"
      data-testid={`section-progress-${job.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-700">
            Section Progress
          </span>
          {loading ? (
            <span className="text-[10px] text-zinc-400">loading…</span>
          ) : (
            <span className="text-[10px] text-zinc-500">
              {completed} of {total} complete
            </span>
          )}
        </div>
        <span className="text-[10px] font-semibold text-zinc-700">{pct}%</span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 w-full rounded-full bg-zinc-100 overflow-hidden mb-3">
        <div
          className={`h-full transition-all duration-500 ${
            pct === 100 ? "bg-emerald-500" : "bg-amber-400"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Pill row (horizontal scroll on mobile) */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {sections.map((s) => {
          const Icon = SECTION_ICONS[s.key] || Circle;
          return (
            <button
              key={s.key}
              onClick={() => scrollToAnchor(s.anchor)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[10px] font-medium transition-all active:scale-95 ${
                s.done
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                  : "bg-white border-zinc-200 text-zinc-600 hover:border-amber-300 hover:bg-amber-50"
              }`}
              data-testid={`section-progress-pill-${s.key}`}
              aria-label={`Jump to ${s.label} section, ${s.done ? "complete" : "incomplete"}`}
            >
              {s.done ? (
                <Check className="w-3 h-3 text-emerald-600" />
              ) : (
                <Icon className="w-3 h-3 text-zinc-500" />
              )}
              <span>{s.label}</span>
              <span className={`text-[9px] font-semibold ${s.done ? "text-emerald-600" : "text-zinc-400"}`}>
                {s.detail}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
